import React, { useState, useEffect, useRef } from 'react';

// Module-level cache of URLs that have already failed.
// This persists across all component instances and re-renders,
// so a broken URL is never retried during the session.
const failedUrlCache = new Set();

/**
 * A robust image component that handles loading errors by falling back to a local placeholder.
 * Uses a module-level cache to remember broken URLs and avoid flickering on re-renders.
 *
 * @param {Object} props - Component props
 * @param {string} props.src - Primary image source
 * @param {string} props.alt - Accessibility text
 * @param {string} props.className - CSS classes
 * @param {string} props.fallbackSrc - Optional custom fallback source
 */
const FALLBACK_IMAGE = '/img/course-placeholder.png';

const ImageWithFallback = ({ src, alt, className, fallbackSrc = FALLBACK_IMAGE, ...props }) => {
    // Determine the initial source: if the URL already failed or is empty, go straight to fallback
    const getInitialSrc = () => {
        if (!src || failedUrlCache.has(src)) {
            return fallbackSrc;
        }
        return src;
    };

    const [imgSrc, setImgSrc] = useState(getInitialSrc);
    const mountedRef = useRef(true);

    // If the parent passes a different `src` prop, re-evaluate
    useEffect(() => {
        if (!src || failedUrlCache.has(src)) {
            setImgSrc(fallbackSrc);
        } else {
            setImgSrc(src);
        }
    }, [src, fallbackSrc]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const handleError = () => {
        if (src) {
            failedUrlCache.add(src); // Remember this URL is broken
        }
        if (mountedRef.current && imgSrc !== fallbackSrc) {
            setImgSrc(fallbackSrc);
        }
    };

    return (
        <img
            src={imgSrc}
            alt={alt}
            className={className}
            onError={handleError}
            loading="lazy"
            {...props}
        />
    );
};

export default ImageWithFallback;
